// Switcher V2.1
// used for resizing a parent when the content has switched to a new element
// also copy _switcher.scss

// init: switcher = new Switcher(); switcher.init();

// usage: 
// <button data-switcher-target="name" data-nr="0"></button>
// <div class="switcher" data-switcher="name" data-can-close="true">
//   <div class="switcher__item" data-switcher="name" data-nr="0">
//   <!-- content -->
//   </div><!-- switcher__item -->
// </div><!-- switcher -->
// add switcher__item--hidden to start an element hidden
// set data-can-close to false to prevent closing the switcher when clicking the same button again, set to true to allow closing it (so all items will be hidden)
// there can be multiple switcher elements with the same name on the page, to replace different content at the same time

// required properties:
// none

// possible properties:
// duration = 400: set to transition in ms, set to --switcher-duration
// startCallback = '': called when the switcher is started, returns a NodeList of the switcher elements that will be changed
// afterFadeOutCallback = '': called after fading out, returns the switcher element
// afterResizeCallback = '': called after resizing when all is done, returns the switcher element

// methods:
// switcher.init(): attach listeners/observer and set the initial height
// switcher.destroy(): remove listeners/observer
// switcher.switchTo(target, nr): programmatically trigger a switch

// changelog:
// 1.0 initial version
// 1.1 added startcallback
// 1.2 startCallback now returns the switcher elements. afterFadeOutCallback and afterResizeCallback now return the switcher element that was changed
// 1.3 replace elFromEvent function with inline event.target.closest calls
// 1.4 before switching, check if previous element's height changed
// 1.4.1 fix safari bug where it doesn't fade correctly
// 1.5 add data-can-close option to switcher element, so if the same button is clicked again, it will close the switcher instead of doing nothing
// 1.5.1 remove timeout when there is no open element, so it switches immediately
// 2.0 breaking: move startup side effects out of the constructor into init(), add destroy(), and split internal logic into private methods
// 2.1 add public switchTo(target, nr) method

export default class Switcher {
  #blockResizeObserver;
  #resizeObserver;
  #boundSetResize;
  #boundObserverHandler;
  #boundHandleClick;
  #boundHandleChange;

  constructor({
    duration = 400,
    startCallback = '',
    afterFadeOutCallback = '',
    afterResizeCallback = '',
  } = '') {
    // console.log('Switcher');

    // user variables -----------------------------------------------
    this.duration = duration;
    this.startCallback = startCallback;
    this.afterFadeOutCallback = afterFadeOutCallback;
    this.afterResizeCallback = afterResizeCallback;

    // code variables -----------------------------------------------
    this.#blockResizeObserver = false;
    this.#resizeObserver = undefined;

    this.#boundSetResize = this.#setResize.bind(this);
    this.#boundObserverHandler = this.#observerHandler.bind(this);
    this.#boundHandleClick = this.#handleClick.bind(this);
    this.#boundHandleChange = this.#handleChange.bind(this);
  }

  init = () => {
    document.body.addEventListener('click', this.#boundHandleClick);
    document.body.addEventListener('change', this.#boundHandleChange);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#boundSetResize);
    }

    this.#setResize();

    const el = document.querySelector('body');
    if (el) {
      this.#resizeObserver = new ResizeObserver(this.#boundObserverHandler);
      this.#resizeObserver.observe(el);
    }
  }

  switchTo = (target, nr) => {
    this.#setSwitcher(target, nr);
  }

  destroy = () => {
    document.body.removeEventListener('click', this.#boundHandleClick);
    document.body.removeEventListener('change', this.#boundHandleChange);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.#boundSetResize);
    }

    if (this.#resizeObserver) this.#resizeObserver.disconnect();
  }

  #setResize() {
    // console.log('setResize');
    const switcherEls = document.querySelectorAll('.switcher');
    switcherEls.forEach(switcherEl => {
      const action = switcherEl.getAttribute('data-switcher');
      const activeItemEl = switcherEl.querySelector(`.switcher__item[data-switcher="${action}"]:not(.switcher__item--hidden)`);
      if (activeItemEl) {
        const activeTextHeight = activeItemEl.offsetHeight;
        // console.log({activeTextHeight});
        switcherEl.style.setProperty('--switcher-scroll-height', activeTextHeight + 'px');
      } else {
        switcherEl.style.setProperty('--switcher-scroll-height', 0 + 'px');
      }
    });
  }

  #observerHandler() {
    // console.log('observerHandler');
    // after possible site specific resize calculations
    if (!this.#blockResizeObserver) setTimeout(this.#boundSetResize, 100);
  }

  #setSwitcher(target, nr) {
    const switcherEls = document.querySelectorAll(`.switcher[data-switcher="${target}"]`);

    if (typeof this.startCallback == 'function') this.startCallback(switcherEls);

    // console.log({target, nr, switcherEls});

    this.#blockResizeObserver = true;

    switcherEls.forEach(switcherEl => {
      const prevItemEl = switcherEl.querySelector(`.switcher__item[data-switcher="${target}"]:not(.switcher__item--hidden)`);
      const prevNr = prevItemEl ? prevItemEl.getAttribute('data-nr') : null;
      const fadeOutDuration = prevItemEl ? this.duration : 0;

      if (prevNr == nr && switcherEl.getAttribute('data-can-close') !== 'true') return;

      if (fadeOutDuration > 0) switcherEl.classList.add('switcher--faded-out');

      // so height is correct before switcher for if content changed
      this.#setResize();

      setTimeout(() => {
        const itemEls = switcherEl.querySelectorAll(`.switcher__item[data-switcher="${target}"]`);
        itemEls.forEach(itemEl => {
          itemEl.classList.add('switcher__item--hidden');
        });

        if (prevNr !== nr) {
          const itemEl = switcherEl.querySelector(`.switcher__item[data-switcher="${target}"][data-nr="${nr}"]`);
          // console.log({itemEl});
          if (itemEl) itemEl.classList.remove('switcher__item--hidden');
        }

        switcherEl.classList.remove('switcher--faded-out');

        switcherEl.classList.add('switcher--resizing'); // resize holder to new size

        requestAnimationFrame(this.#boundSetResize); // wuut

        if (typeof this.afterFadeOutCallback == 'function') {
          this.afterFadeOutCallback(switcherEl);
        }
      }, fadeOutDuration);

      setTimeout(() => {
        switcherEl.classList.remove('switcher--resizing');
        this.#blockResizeObserver = false;

        if (typeof this.afterResizeCallback == 'function') {
          this.afterResizeCallback(switcherEl);
        }
      }, fadeOutDuration + this.duration);
    });
  }

  #handleClick(event) {
    const el = event.target.closest('button[data-switcher-target], input[data-switcher-target]');
    if (!el) return;

    const target = el.getAttribute('data-switcher-target');
    const nr = el.getAttribute('data-nr');
    const hasClosableSwitcher = document.querySelector(`.switcher[data-switcher="${target}"][data-can-close="true"]`) !== null;

    const otherEls = document.querySelectorAll(`button[data-switcher-target="${target}"], input[data-switcher-target="${target}"]`);
    otherEls.forEach(otherEl => {
      if (otherEl == el) {
        otherEl.dataset.active = el.dataset.active === '1' && hasClosableSwitcher ? '0' : '1';
      } else {
        otherEl.dataset.active = '0';
      }
    });

    this.#setSwitcher(target, nr);
  }

  #handleChange(event) {
    const el = event.target.closest('select[data-switcher-target]');
    if (!el) return;

    const target = el.getAttribute('data-switcher-target');
    const nr = el.value;
    this.#setSwitcher(target, nr);
  }
}


